<script setup lang="ts">
  import { computed, ref } from 'vue'
  import { type SuperUserQuestionnaire as Questionnaire } from '@/types/generated'

  import { PlusIcon, ChevronDownIcon, ChevronUpIcon, TrashIcon } from '@heroicons/vue/24/outline'
  import InputField from '@/components/admin/form/InputField.vue'
  import { useQuestionnaireEditorStore } from '@/store/admin/questionnaire/questionnaireEditor'
  import QuestionnaireQuestion from '@/components/admin/questionnaire/editor/QuestionnaireEditorQuestion.vue'

  const questionnaireEditorStore = useQuestionnaireEditorStore()
  const questionnaire = computed<Questionnaire | null>(() => questionnaireEditorStore.questionnaire)

  const props = defineProps({
    uuid: {
      type: String,
      required: true
    }
  })
  const section = computed(() => questionnaire.value?.schema.find((p) => p.uuid === props.uuid))

  const isExpanded = ref(true)
</script>
<template>
  <div
    v-if="section"
    class="mb-4 mt-4 rounded-xl border bg-slate-50 p-4 shadow-sm md:p-5"
  >
    <div class="mt-1 flex items-center space-x-4">
      <div class="flex-1">
        <InputField
          v-model="section.title"
          name="title"
        />
      </div>
      <div>
        <button
          type="button"
          class="inline-flex items-center gap-x-2 rounded-lg border border-transparent px-4 py-3 text-sm font-medium
            text-gray-800 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none disabled:pointer-events-none
            disabled:opacity-50 dark:text-white dark:hover:bg-neutral-700 dark:focus:bg-neutral-700"
          @click="isExpanded = !isExpanded"
        >
          {{ section.items.length }} {{ section.items.length === 1 ? 'question' : 'questions' }}
          <ChevronUpIcon
            v-if="isExpanded"
            class="size-4 shrink-0"
          />
          <ChevronDownIcon
            v-else
            class="size-4 shrink-0"
          />
        </button>
      </div>
    </div>
    <div v-if="isExpanded">
      <button
        v-if="section.items.length > 0"
        type="button"
        class="mt-1 inline-flex items-center gap-x-2 rounded-lg border border-transparent px-4 py-3 text-sm
          font-medium text-gray-800 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none
          disabled:pointer-events-none disabled:opacity-50 dark:text-white dark:hover:bg-neutral-700
          dark:focus:bg-neutral-700"
        @click="questionnaireEditorStore.addQuestion(section, 0)"
      >
        <PlusIcon class="size-4 shrink-0" />
        Add question
      </button>
      <div
        v-for="(question, index) in section.items"
        :key="index"
      >
        <QuestionnaireQuestion
          :uuid="question.uuid"
          :section-uuid="section.uuid"
        />
        <div
          v-if="index + 1 < section.items.length"
          class="flex items-center justify-between"
        >
          <button
            type="button"
            class="mt-1 inline-flex items-center gap-x-2 rounded-lg border border-transparent px-4 py-3 text-sm
              font-medium text-gray-800 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none
              disabled:pointer-events-none disabled:opacity-50 dark:text-white dark:hover:bg-neutral-700
              dark:focus:bg-neutral-700"
            @click="questionnaireEditorStore.addQuestion(section, index + 1)"
          >
            <PlusIcon class="size-4 shrink-0" />
            Add question
          </button>
        </div>
      </div>
      <div class="flex items-center justify-between pt-2">
        <button
          type="button"
          class="mt-1 inline-flex items-center gap-x-2 rounded-lg border border-transparent px-4 py-3 text-sm
            font-medium text-gray-800 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none
            disabled:pointer-events-none disabled:opacity-50 dark:text-white dark:hover:bg-neutral-700
            dark:focus:bg-neutral-700"
          @click="questionnaireEditorStore.addQuestion(section)"
        >
          <PlusIcon class="size-4 shrink-0" />
          Add question
        </button>
        <button
          type="button"
          class="ms-2 inline-flex items-center gap-x-2 rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm
            font-medium text-red-600 shadow-sm hover:bg-gray-50 focus:bg-gray-50 focus:outline-none
            disabled:pointer-events-none disabled:opacity-50"
          @click="questionnaireEditorStore.removeSection(section)"
        >
          <TrashIcon class="size-4 shrink-0" />
          Remove section
        </button>
      </div>
    </div>
  </div>
</template>
